<script setup lang="ts">
import { computed, reactive, ref, watch } from "vue";
import {
  UiDialog,
  UiIcon,
  UiInputField,
  UiNumberField,
  UiSegmentedControl,
  UiSelect,
  UiText,
  useToaster,
} from "@shopexperts/shared";

import {
  addExpertBonusSignal,
  updateExpertBonusSignal,
  type ExpertBonusSignal,
  type ExpertBonusSignalPresetOption,
} from "@/app/services/confidenceSignalsApi.service";

const props = defineProps<{
  expertId: number;
  expertName: string;
  /** Group options for custom bonus signals, in configured group order. */
  groupOptions: { label: string; value: string }[];
  /** Presets already filtered to this expert's type, each flagged `attached`. */
  presets: ExpertBonusSignalPresetOption[];
  /** A custom bonus signal being edited, or null when adding. */
  editing: ExpertBonusSignal | null;
}>();

const emit = defineEmits<{ (e: "saved"): void }>();

const open = defineModel<boolean>({ default: false });

const toaster = useToaster();
const mode = ref<"preset" | "custom">("preset");
const selectedPresetKey = ref<string | null>(null);
const presetSearch = ref("");
const isSaving = ref(false);

const form = reactive({
  group_key: "",
  name: "",
  public_text: "",
  points: "0",
});

const isEditing = computed(() => props.editing !== null);

const modeOptions = [
  { label: "From preset", value: "preset" as const },
  { label: "Custom", value: "custom" as const },
];

const availablePresets = computed(() => {
  const query = presetSearch.value.trim().toLowerCase();
  if (!query) return props.presets;
  return props.presets.filter((preset) =>
    [preset.key, preset.name, preset.public_text].some((value) =>
      value.toLowerCase().includes(query),
    ),
  );
});

const groupLabels = computed(
  () => new Map(props.groupOptions.map((option) => [option.value, option.label])),
);

watch(open, (isOpen) => {
  if (!isOpen) return;

  presetSearch.value = "";
  selectedPresetKey.value = null;

  if (props.editing) {
    mode.value = "custom";
    form.group_key = props.editing.group_key;
    form.name = props.editing.name ?? "";
    form.public_text = props.editing.public_text;
    form.points = String(props.editing.points);
    return;
  }

  // Nothing to pick from means the custom form is the only usable mode.
  mode.value = props.presets.length > 0 ? "preset" : "custom";
  form.group_key = props.groupOptions[0]?.value ?? "";
  form.name = "";
  form.public_text = "";
  form.points = "0";
});

const dialogTitle = computed(() =>
  isEditing.value ? "Edit bonus signal" : "Add bonus signal",
);

const dialogText = computed(() =>
  isEditing.value
    ? "Changes apply to this expert only."
    : `Bonus points only apply while they fit under the group's ceiling, so a large bonus may score nothing for ${props.expertName}.`,
);

const primaryActionLabel = computed(() => {
  if (isEditing.value) return isSaving.value ? "Saving..." : "Save changes";
  if (mode.value === "preset") return isSaving.value ? "Attaching..." : "Attach preset";
  return isSaving.value ? "Adding..." : "Add bonus signal";
});

async function submit() {
  if (isSaving.value) return;

  if (isEditing.value) {
    await saveCustomEdit();
    return;
  }

  if (mode.value === "preset") {
    await attachPreset();
    return;
  }

  await createCustom();
}

async function attachPreset() {
  const presetKey = selectedPresetKey.value;
  if (!presetKey) {
    toaster.create({ title: "Pick a bonus signal to attach", type: "error" });
    return;
  }

  isSaving.value = true;
  try {
    await addExpertBonusSignal(props.expertId, { preset_key: presetKey });
    emit("saved");
    open.value = false;
    toaster.create({ title: "Bonus signal attached", type: "success" });
  } catch (error) {
    console.error("[BonusSignals] Failed to attach preset", error);
  } finally {
    isSaving.value = false;
  }
}

function validateCustom(): boolean {
  if (!form.group_key) {
    toaster.create({ title: "Pick a group for this bonus signal", type: "error" });
    return false;
  }

  if (!form.public_text.trim()) {
    toaster.create({ title: "Public text is required", type: "error" });
    return false;
  }

  // Number("") is 0, so an empty field would otherwise pass as a valid zero.
  // The API takes an integer, so reject fractions here rather than on a 422.
  const points = Number(form.points);
  if (form.points.trim() === "" || !Number.isInteger(points) || points < 0) {
    toaster.create({ title: "Points must be a whole number, zero or greater", type: "error" });
    return false;
  }

  return true;
}

async function createCustom() {
  if (!validateCustom()) return;

  isSaving.value = true;
  try {
    await addExpertBonusSignal(props.expertId, {
      group_key: form.group_key,
      // `name` is the internal label; the API falls back to public text.
      name: form.name.trim() || undefined,
      public_text: form.public_text.trim(),
      points: Number(form.points),
    });
    emit("saved");
    open.value = false;
    toaster.create({ title: "Bonus signal added", type: "success" });
  } catch (error) {
    console.error("[BonusSignals] Failed to add custom bonus signal", error);
  } finally {
    isSaving.value = false;
  }
}

async function saveCustomEdit() {
  const editing = props.editing;
  if (!editing || !validateCustom()) return;

  isSaving.value = true;
  try {
    await updateExpertBonusSignal(props.expertId, editing.id, {
      group_key: form.group_key,
      // null clears it rather than persisting public text as a derived name;
      // the API falls back to public text when reading it back.
      name: form.name.trim() || null,
      public_text: form.public_text.trim(),
      points: Number(form.points),
    });
    emit("saved");
    open.value = false;
    toaster.create({ title: "Bonus signal updated", type: "success" });
  } catch (error) {
    console.error("[BonusSignals] Failed to update bonus signal", error);
  } finally {
    isSaving.value = false;
  }
}
</script>

<template>
  <UiDialog
    v-model="open"
    :title="dialogTitle"
    :text="dialogText"
    :primary-action-label="primaryActionLabel"
    secondary-action-label="Cancel"
    size="lg"
    :loading="isSaving"
    @primary-action="submit"
    @secondary-action="open = false"
  >
    <div class="flex flex-col gap-4 text-left">
      <UiSegmentedControl
        v-if="!isEditing"
        v-model="mode"
        :options="modeOptions"
        name="bonus-signal-mode"
      />

      <template v-if="!isEditing && mode === 'preset'">
        <div v-if="presets.length === 0" class="rounded-lg border border-dashed border-gray-300 px-4 py-8 text-center">
          <UiIcon name="star" class="mx-auto mb-2 h-6 w-6 text-gray-300" />
          <UiText as="p" variant="p-small" class="text-gray-500">
            No presets apply to this expert type yet. Create one under Trust Signals, or switch to Custom.
          </UiText>
        </div>

        <template v-else>
          <UiInputField
            v-model="presetSearch"
            icon="search"
            name="bonus-signal-preset-search"
            placeholder="Search presets"
          />

          <div class="flex max-h-72 flex-col gap-2 overflow-y-auto">
            <button
              v-for="preset in availablePresets"
              :key="preset.key"
              type="button"
              :aria-pressed="selectedPresetKey === preset.key"
              class="flex items-start justify-between gap-3 rounded-lg border px-4 py-3 text-left transition-colors"
              :class="[
                preset.attached
                  ? 'cursor-not-allowed border-gray-200 bg-gray-50 opacity-60'
                  : 'cursor-pointer hover:border-gray-400',
                selectedPresetKey === preset.key
                  ? 'border-emerald-600 bg-emerald-50/60'
                  : 'border-gray-200',
              ]"
              :disabled="preset.attached"
              @click="selectedPresetKey = preset.key"
            >
              <div class="min-w-0">
                <div class="flex flex-wrap items-center gap-2">
                  <span class="text-sm font-semibold text-gray-900">{{ preset.name }}</span>
                  <span
                    v-if="preset.attached"
                    class="rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600"
                  >Already added</span>
                  <span
                    v-else-if="preset.status === 'disabled'"
                    class="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800"
                  >Globally disabled</span>
                </div>
                <p class="mt-0.5 truncate text-xs text-gray-500">{{ preset.public_text }}</p>
                <p class="mt-1 text-[11px] text-gray-400">
                  {{ groupLabels.get(preset.group_key) ?? preset.group_key }}
                </p>
              </div>
              <span class="shrink-0 text-sm font-semibold tabular-nums text-emerald-700">
                +{{ preset.points }}
              </span>
            </button>

            <p
              v-if="availablePresets.length === 0"
              class="px-1 py-6 text-center text-sm text-gray-500"
            >
              No presets match your search.
            </p>
          </div>
        </template>
      </template>

      <template v-else>
        <div>
          <label for="bonus-signal-group" class="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Group</label>
          <UiSelect
            :model-value="form.group_key"
            name="bonus-signal-group"
            :options="groupOptions"
            @update:model-value="(value) => { if (typeof value === 'string') form.group_key = value }"
          />
          <p class="mt-1 text-[11px] text-gray-400">
            The bonus competes for headroom in this group only.
          </p>
        </div>

        <div>
          <label for="bonus-signal-name" class="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">
            Internal name <span class="font-normal normal-case text-gray-400">(optional)</span>
          </label>
          <UiInputField
            v-model="form.name"
            name="bonus-signal-name"
            placeholder="Keynote at Shopify Unite"
          />
        </div>

        <div>
          <label for="bonus-signal-public-text" class="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Public text</label>
          <UiInputField
            v-model="form.public_text"
            name="bonus-signal-public-text"
            placeholder="Keynote speaker, Shopify Unite 2025"
          />
        </div>

        <div class="sm:w-40">
          <label for="bonus-signal-points" class="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">Points</label>
          <UiNumberField
            :model-value="form.points"
            name="bonus-signal-points"
            :min="0"
            @update:model-value="(value) => { form.points = value ?? '0' }"
          />
        </div>
      </template>
    </div>
  </UiDialog>
</template>
